// src/pages/JoinChallenge.tsx
// Handles challenge invite links: /join-challenge/:challengeId
'use client';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSupabase, currentUser } from "@/lib/supabase";
import { Loader2, Target, Check, X, LogIn, Trophy, Calendar, Users } from 'lucide-react';
import { format, differenceInDays } from 'date-fns';

export default function JoinChallenge() {
  const { challengeId } = useParams<{ challengeId: string }>();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState<'loading' | 'not-logged-in' | 'preview' | 'joining' | 'success' | 'error' | 'already-joined'>('loading');
  const [challenge, setChallenge] = useState<any>(null);
  const [participantCount, setParticipantCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    loadChallenge();
  }, [challengeId]);

  const loadChallenge = async () => {
    if (!challengeId) {
      setStatus('error');
      setErrorMessage('Invalid challenge link');
      return;
    }

    const supabase = getSupabase();
    
    // Get challenge details
    const { data: challengeData, error } = await supabase
      .from('challenges')
      .select('*')
      .eq('id', challengeId)
      .single();

    if (error || !challengeData) {
      setStatus('error');
      setErrorMessage('Challenge not found');
      return;
    }

    setChallenge(challengeData);

    // Get participant count
    const { count } = await supabase
      .from('challenge_participants')
      .select('*', { count: 'exact', head: true })
      .eq('challenge_id', challengeId);

    setParticipantCount(count || 0);

    // Check if user is logged in
    const user = await currentUser();
    
    if (!user) {
      localStorage.setItem('pendingChallengeInvite', challengeId);
      setStatus('not-logged-in');
      return;
    }

    // Check if already participating
    const { data: existingParticipation } = await supabase
      .from('challenge_participants')
      .select('id')
      .eq('challenge_id', challengeId)
      .eq('user_id', user.id)
      .single();

    if (existingParticipation) {
      setStatus('already-joined');
      return;
    }

    // Check if challenge has ended
    if (new Date(challengeData.end_date) < new Date()) {
      setStatus('error');
      setErrorMessage('This challenge has already ended');
      return;
    }

    setStatus('preview');
  };

  const joinChallenge = async () => {
    const user = await currentUser();
    if (!user || !challengeId) return;

    setStatus('joining');

    const supabase = getSupabase();
    const { error } = await supabase
      .from('challenge_participants')
      .insert({
        challenge_id: challengeId,
        user_id: user.id,
        score: 0
      });

    if (error) {
      console.error('Error joining challenge:', error);
      setStatus('error');
      setErrorMessage('Failed to join challenge. Please try again.');
      return;
    }

    setStatus('success');
  };

  const goToLogin = () => {
    if (challengeId) {
      localStorage.setItem('pendingChallengeInvite', challengeId);
    }
    navigate('/login');
  };

  const goToSignup = () => {
    if (challengeId) {
      localStorage.setItem('pendingChallengeInvite', challengeId);
    }
    navigate('/signup');
  };

  const goToLeaderboard = () => {
    navigate('/leaderboard');
  };

  const getChallengeTypeLabel = (type: string) => {
    switch (type) {
      case 'adherence': return '📊 Highest Adherence %';
      case 'streak': return '🔥 Longest Streak';
      case 'calories': return '🎯 Calorie Target Hits';
      case 'workouts': return '💪 Most Workouts';
      default: return type;
    }
  };

  const daysLeft = challenge ? differenceInDays(new Date(challenge.end_date), new Date()) : 0;

  return (
    <div className="min-h-screen bg-[#ffffff] flex items-center justify-center p-4">
      <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-8 max-w-md w-full text-center">
        
        {/* Loading */}
        {status === 'loading' && (
          <div className="py-8">
            <Loader2 className="w-12 h-12 animate-spin text-green-400 mx-auto mb-4" />
            <p className="text-gray-500">Loading challenge...</p>
          </div>
        )}

        {/* Not Logged In */}
        {status === 'not-logged-in' && challenge && (
          <div className="py-4">
            <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trophy className="w-8 h-8 text-yellow-400" />
            </div>
            <h1 className="text-2xl font-bold text-[#0f172a] mb-2">You're Invited!</h1>
            <p className="text-gray-500 mb-4">Join the challenge:</p>
            
            {/* Challenge Preview */}
            <div className="bg-[#f8fafc] rounded-xl p-4 mb-6 text-left">
              <h2 className="font-bold text-lg text-[#0f172a] mb-2">{challenge.name}</h2>
              <div className="space-y-2 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  {getChallengeTypeLabel(challenge.type)}
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {daysLeft > 0 ? `${daysLeft} days left` : 'Ends today'}
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  {participantCount} participant{participantCount !== 1 ? 's' : ''}
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={goToSignup}
                className="w-full px-6 py-3 bg-green-500 hover:bg-green-400 text-[#0f172a] font-bold rounded-xl transition"
              >
                Create Account to Join
              </button>
              <button
                onClick={goToLogin}
                className="w-full px-6 py-3 bg-[#f1f5f9] hover:bg-[#f1f5f9] text-[#0f172a] font-medium rounded-xl transition"
              >
                I already have an account
              </button>
            </div>
          </div>
        )}

        {/* Preview (Logged In) */}
        {status === 'preview' && challenge && (
          <div className="py-4">
            <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trophy className="w-8 h-8 text-yellow-400" />
            </div>
            <h1 className="text-2xl font-bold text-[#0f172a] mb-2">Join Challenge</h1>
            
            {/* Challenge Preview */}
            <div className="bg-[#f8fafc] rounded-xl p-4 mb-6 text-left">
              <h2 className="font-bold text-lg text-[#0f172a] mb-2">{challenge.name}</h2>
              <div className="space-y-2 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  {getChallengeTypeLabel(challenge.type)}
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {daysLeft > 0 ? `${daysLeft} days left` : 'Ends today'}
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  {participantCount} participant{participantCount !== 1 ? 's' : ''}
                </div>
              </div>
            </div>

            <button
              onClick={joinChallenge}
              className="w-full px-6 py-3 bg-green-500 hover:bg-green-400 text-[#0f172a] font-bold rounded-xl transition"
            >
              Join Challenge
            </button>
          </div>
        )}

        {/* Joining */}
        {status === 'joining' && (
          <div className="py-8">
            <Loader2 className="w-12 h-12 animate-spin text-green-400 mx-auto mb-4" />
            <p className="text-gray-500">Joining challenge...</p>
          </div>
        )}

        {/* Success */}
        {status === 'success' && (
          <div className="py-4">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-400" />
            </div>
            <h1 className="text-2xl font-bold text-[#0f172a] mb-2">You're In!</h1>
            <p className="text-gray-500 mb-6">
              You've joined "{challenge?.name}". Good luck!
            </p>
            <button
              onClick={goToLeaderboard}
              className="w-full px-6 py-3 bg-green-500 hover:bg-green-400 text-[#0f172a] font-bold rounded-xl transition"
            >
              View Challenge
            </button>
          </div>
        )}

        {/* Already Joined */}
        {status === 'already-joined' && (
          <div className="py-4">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-400" />
            </div>
            <h1 className="text-2xl font-bold text-[#0f172a] mb-2">Already Participating!</h1>
            <p className="text-gray-500 mb-6">
              You're already in this challenge. Keep going!
            </p>
            <button
              onClick={goToLeaderboard}
              className="w-full px-6 py-3 bg-green-500 hover:bg-green-400 text-[#0f172a] font-bold rounded-xl transition"
            >
              View Challenge
            </button>
          </div>
        )}

        {/* Error */}
        {status === 'error' && (
          <div className="py-4">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <X className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-[#0f172a] mb-2">Oops!</h1>
            <p className="text-gray-500 mb-6">{errorMessage}</p>
            <button
              onClick={goToLeaderboard}
              className="w-full px-6 py-3 bg-[#f1f5f9] hover:bg-[#f1f5f9] text-[#0f172a] font-medium rounded-xl transition"
            >
              Go to Leaderboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
